let pendingDrag = null;
let dragData = null;
let ghostEl = null;

const DRAG_THRESHOLD = 12;
const SCROLL_BIAS = 2.5;
const TAP_MAX_DURATION = 250;

function createGhost(src, x, y) {
  const el = document.createElement('img');
  el.src = src;
  Object.assign(el.style, {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: '9999',
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px',
    opacity: '0.85',
    left: `${x - 40}px`,
    top: `${y - 40}px`,
    transform: 'scale(1.1)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    transition: 'none',
  });
  document.body.appendChild(el);
  return el;
}

function removeGhost() {
  if (ghostEl) {
    ghostEl.remove();
    ghostEl = null;
  }
}

function findOnDrop(el) {
  const fiberKey = Object.keys(el).find(
    (k) => k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
  );
  if (!fiberKey) return null;
  let fiber = el[fiberKey];
  while (fiber) {
    const props = fiber.memoizedProps || fiber.pendingProps;
    if (props?.onDrop) return props.onDrop;
    fiber = fiber.return;
  }
  return null;
}

function findDropTarget(x, y) {
  if (ghostEl) ghostEl.style.display = 'none';
  let el = document.elementFromPoint(x, y);
  if (ghostEl) ghostEl.style.display = '';
  while (el && el !== document.body) {
    const handler = findOnDrop(el);
    if (handler) return { el, handler };
    el = el.parentElement;
  }
  return null;
}

function makeFakeDropEvent(targetEl, url, type) {
  return {
    preventDefault: () => {},
    stopPropagation: () => {},
    currentTarget: targetEl,
    target: targetEl,
    dataTransfer: {
      getData(key) {
        const urlKeys = [
          'application/x-generated-image',
          'text/uri-list',
          'text/plain',
          'imageUrl',
        ];
        if (urlKeys.includes(key)) return url;
        if (key === 'imageType') return type;
        return '';
      },
      files: { length: 0, item: () => null },
      items: { length: 0 },
      dropEffect: 'copy',
    },
  };
}

export function initTouchDrag() {
  // non-passive so we can call preventDefault to block scroll during drag
  document.addEventListener(
    'touchstart',
    (e) => {
      const target = e.target.closest('[draggable]');
      if (!target) return;
      const url = target.dataset.dragUrl;
      if (!url) return;

      const touch = e.touches[0];
      pendingDrag = {
        url,
        type: target.dataset.dragType || 'uploaded',
        startX: touch.clientX,
        startY: touch.clientY,
        element: target,
        startTime: Date.now(),
      };
      // prevent browser from starting scroll or generating click chain
      e.preventDefault();
    },
    { passive: false }
  );

  document.addEventListener(
    'touchmove',
    (e) => {
      if (!pendingDrag && !dragData) return;
      const touch = e.touches[0];

      if (pendingDrag && !dragData) {
        const dx = touch.clientX - pendingDrag.startX;
        const dy = touch.clientY - pendingDrag.startY;
        if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
          if (Math.abs(dy) > Math.abs(dx) * SCROLL_BIAS) {
            // mostly vertical = scroll intent, cancel drag
            pendingDrag = null;
            return;
          }
          dragData = { url: pendingDrag.url, type: pendingDrag.type };
          pendingDrag = null;
          ghostEl = createGhost(dragData.url, touch.clientX, touch.clientY);
          if (navigator.vibrate) navigator.vibrate(40);
        }
      }

      if (dragData && ghostEl) {
        ghostEl.style.left = `${touch.clientX - 40}px`;
        ghostEl.style.top = `${touch.clientY - 40}px`;
        // cancelable is always true here because touchstart called preventDefault
        e.preventDefault();
      }
    },
    { passive: false }
  );

  document.addEventListener('touchend', (e) => {
    const pending = pendingDrag;
    pendingDrag = null;

    if (!dragData) {
      // simulate click if it was a quick tap (touchstart called preventDefault
      // which suppressed the browser-generated click event)
      if (pending) {
        const elapsed = Date.now() - pending.startTime;
        const touch = e.changedTouches[0];
        const dist = Math.hypot(
          touch.clientX - pending.startX,
          touch.clientY - pending.startY
        );
        if (elapsed < TAP_MAX_DURATION && dist < DRAG_THRESHOLD) {
          pending.element.dispatchEvent(
            new MouseEvent('click', { bubbles: true, cancelable: true })
          );
        }
      }
      return;
    }

    const touch = e.changedTouches[0];
    removeGhost();

    const target = findDropTarget(touch.clientX, touch.clientY);
    if (target) {
      const fakeEvent = makeFakeDropEvent(target.el, dragData.url, dragData.type);
      target.handler(fakeEvent);
    }

    dragData = null;
  });

  document.addEventListener('touchcancel', () => {
    pendingDrag = null;
    dragData = null;
    removeGhost();
  });
}
